/**
 * n8nServiceMock — the `n8n_proxy_mock()`.
 *
 * Mirrors the read-side of the real N8NService (getCampaignMetrics,
 * getLeadsCards, getLead): same argument shape, same return type
 * (N8NActionResponse<T>), so a caller cannot tell — from the contract — whether
 * it is talking to the real proxy or this mock. It never touches the Appwrite
 * Function or the N8N webhook; everything is generated locally.
 *
 * The generated dataset is cached per (volume × userEmail) and only
 * regenerated when one of those changes, so the Kanban stays stable across
 * re-renders and `getLead` can resolve the matching detail object.
 */

import type {
  CampaignMetricsResponse,
  CampaignMetrics,
  LeadsCardsResponse,
  LeadCardResponse,
  GetLeadResponse,
} from '../../types/n8nResponses';
import { mockDataVolumeStore, type MockVolume } from './mockDataVolume';
import {
  generateMockCampaigns,
  CAMPAIGN_COUNT_BY_VOLUME,
} from './generators/campaignGenerator';
import { generateMockLeadsForAllCampaigns } from './generators/leadCardGenerator';
import { generateMockLeadDetail } from './generators/leadDetailGenerator';

export interface N8NActionResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}

interface CachedDataset {
  volume: MockVolume;
  userEmail: string;
  campaigns: CampaignMetrics[];
  leadsByCampaign: Record<string, LeadCardResponse[]>;
}

/** Simulated network latency so loading states render realistically. */
const MOCK_LATENCY_MS = 150;

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), MOCK_LATENCY_MS));

const ok = <T>(data: T): N8NActionResponse<T> => ({
  success: true,
  data,
  timestamp: new Date().toISOString(),
});

const emptyCampaignsResponse = (): CampaignMetricsResponse => ({
  campaigns: [],
  team_members: [],
  team_invitations: [],
});

class N8NServiceMock {
  private cache: CachedDataset | null = null;

  /** Resolve (or rebuild) the dataset for the current volume + user. */
  private resolveDataset(userEmail: string): CachedDataset {
    const volume = mockDataVolumeStore.getVolume();
    if (
      this.cache &&
      this.cache.volume === volume &&
      this.cache.userEmail === userEmail
    ) {
      return this.cache;
    }

    // `none` → empty dataset. Exercises every empty-state in the UI.
    if (volume === 'none') {
      this.cache = {
        volume,
        userEmail,
        campaigns: [],
        leadsByCampaign: {},
      };
      return this.cache;
    }

    const count = CAMPAIGN_COUNT_BY_VOLUME[volume];
    const campaigns = generateMockCampaigns(count, userEmail);
    const leadsByCampaign = generateMockLeadsForAllCampaigns(campaigns, volume);
    this.cache = { volume, userEmail, campaigns, leadsByCampaign };
    return this.cache;
  }

  // -------------------------------------------------------------------------
  // Read actions — mirror N8NService signatures & return types exactly.
  // -------------------------------------------------------------------------

  /** `campaign_metrics` action. */
  async getCampaignMetrics(data: {
    client_email: string;
  }): Promise<N8NActionResponse<CampaignMetricsResponse>> {
    try {
      const dataset = this.resolveDataset(data.client_email);
      return delay(ok<CampaignMetricsResponse>({
        campaigns: dataset.campaigns,
        team_members: [],
        team_invitations: [],
      }));
    } catch (error: any) {
      return delay({ success: false, error: error?.message || 'Mock campaign_metrics failed', timestamp: new Date().toISOString() });
    }
  }

  /** `leads_cards` action. Returns the same shape n8n returns (campaigns[] of leads). */
  async getLeadsCards(data: {
    campaign_id: string;
    client_email: string;
  }): Promise<N8NActionResponse<LeadsCardsResponse>> {
    try {
      const dataset = this.resolveDataset(data.client_email);
      const leads = dataset.leadsByCampaign[data.campaign_id] || [];
      const campaign = dataset.campaigns.find((c) => c.campaign_id === data.campaign_id);
      return delay(ok<LeadsCardsResponse>({
        // Mirrors the real N8N "returns ALL campaigns" quirk the fetcher handles.
        campaign_id: data.campaign_id,
        campaign_name: campaign?.campaign_name || '',
        campaigns: [{
          campaign_id: data.campaign_id,
          campaign_name: campaign?.campaign_name || '',
          leads,
        }],
        leads,
      } as LeadsCardsResponse));
    } catch (error: any) {
      return delay({ success: false, error: error?.message || 'Mock leads_cards failed', timestamp: new Date().toISOString() });
    }
  }

  /** `get_lead` action. */
  async getLead(data: {
    lead_id: string;
    campaign_id: string;
    client_email: string;
  }): Promise<N8NActionResponse<GetLeadResponse>> {
    try {
      const dataset = this.resolveDataset(data.client_email);
      const campaignLeads = dataset.leadsByCampaign[data.campaign_id] || [];
      const card = campaignLeads.find((l) => l.lead_id === data.lead_id);
      if (!card) {
        return delay({ success: false, error: `Mock lead ${data.lead_id} not found`, timestamp: new Date().toISOString() });
      }
      return delay(ok<GetLeadResponse>(generateMockLeadDetail(card)));
    } catch (error: any) {
      return delay({ success: false, error: error?.message || 'Mock get_lead failed', timestamp: new Date().toISOString() });
    }
  }

  /** Drop the cache (e.g. when the user flips the volume switch). Exposed for tests. */
  invalidate(): void {
    this.cache = null;
  }
}

export const n8nServiceMock = new N8NServiceMock();

// Invalidate the cache whenever the volume changes so the next read regenerates
// at the new size. This keeps the UI live-reactive to the switch.
mockDataVolumeStore.subscribe(() => n8nServiceMock.invalidate());
